/**
 * The slice of OTLP/JSON (`ExportLogsServiceRequest`) one request of log records needs.
 *
 * Hand-built on purpose. One record shape, sent by beacon once per failure, does not
 * justify the OpenTelemetry JS SDK in the bundle: its batching, retries and processors
 * solve problems this emitter does not have, and none of them sends a beacon. The counter
 * (`metrics.ts`) is built the same way. Revisit if a signal appears that needs what the SDK
 * has: then `@opentelemetry/sdk-logs` + `@opentelemetry/exporter-logs-otlp-http` replace
 * this file and the callers stay.
 *
 * Wire format: https://opentelemetry.io/docs/specs/otlp/#json-protobuf-encoding
 */

/**
 * A number that travels as `doubleValue` whatever it holds. A plain `number` is an int64
 * when whole and a double otherwise, so a key whose values are only sometimes whole
 * (`downlink_mbps`: 1.45, then 10 at Chromium's cap) would ship under two types and land
 * in two columns on a backend that types attributes per key. Wrapping fixes the type to
 * the key.
 */
export interface DoubleValue {
  readonly double: number;
}

export function double(value: number): DoubleValue {
  return { double: value };
}

export type AttributeValue = string | number | boolean | DoubleValue;

/** Attributes with `undefined` or empty-string values are dropped, not sent as empty. */
export type Attributes = Record<string, AttributeValue | undefined>;

export interface LogRecordInput {
  /** Unix epoch, milliseconds. */
  timestamp: number;
  severityNumber: number;
  severityText: string;
  body: string;
  attributes: Attributes;
}

interface AnyValue {
  stringValue?: string;
  intValue?: string;
  doubleValue?: number;
  boolValue?: boolean;
}

interface KeyValue {
  key: string;
  value: AnyValue;
}

/** `SeverityNumber.ERROR` from the OTel logs data model. */
export const SEVERITY_ERROR = 17;

export const SCOPE_NAME = 'wlabel.widget-load-failures';

function toAnyValue(value: AttributeValue): AnyValue {
  switch (typeof value) {
    case 'boolean':
      return { boolValue: value };
    case 'number':
      // int64 travels as a string in the JSON mapping. A fraction can only be a double; a
      // key that may hold one wraps every value in `double()` rather than rely on this.
      return Number.isInteger(value) ? { intValue: String(value) } : { doubleValue: value };
    case 'object':
      return { doubleValue: value.double };
    default:
      return { stringValue: value };
  }
}

export function toKeyValues(attributes: Attributes): KeyValue[] {
  return Object.entries(attributes)
    .filter((entry): entry is [string, AttributeValue] => entry[1] !== undefined && entry[1] !== '')
    .map(([key, value]) => ({ key, value: toAnyValue(value) }));
}

function toLogRecord(record: LogRecordInput) {
  return {
    timeUnixNano: `${record.timestamp}000000`,
    severityNumber: record.severityNumber,
    severityText: record.severityText,
    body: { stringValue: record.body },
    attributes: toKeyValues(record.attributes)
  };
}

/** One request: every record under the same resource and scope, in the order given. */
export function buildLogsRequest(resource: Attributes, records: readonly LogRecordInput[]) {
  return {
    resourceLogs: [
      {
        resource: { attributes: toKeyValues(resource) },
        scopeLogs: [{ scope: { name: SCOPE_NAME }, logRecords: records.map(toLogRecord) }]
      }
    ]
  };
}
